// src/components/customer-detail/recent-activity-panel.tsx

import { useState } from 'react';
import { FileText, Loader2, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCustomerNotes, useAddCustomerNote } from '@/hooks/useCustomerNotes';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mockUsers } from '@/lib/mock-users';

interface RecentActivityPanelProps {
  customerId: string;
}

export function RecentActivityPanel({ customerId }: RecentActivityPanelProps) {
  const [note, setNote] = useState('');
  const { data: notes = [], isLoading } = useCustomerNotes(customerId);

  const addNoteMutation = useAddCustomerNote();
  const queryClient = useQueryClient();
  const currentUser = mockUsers[0];

  const handleAddNote = async () => {
    if (!note.trim()) return;

    try {
      await addNoteMutation.mutateAsync({
        customerId,
        content: note.trim(),
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`,
      });

      setNote('');
      toast.success('Note added successfully');
      queryClient.invalidateQueries({ queryKey: ['customer-notes', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer-audit-logs', customerId] });
    } catch (error) {
      toast.error('Failed to add note');
      console.error('Error adding note:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const latestNotes = notes.slice(0, 4);

  const renderNotesContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (latestNotes.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-4">
          No notes yet
        </p>
      );
    }

    return latestNotes.map((noteItem, index) => (
      <div key={noteItem.id} className="relative">
        {index < latestNotes.length - 1 && (
          <div className="absolute left-[15px] top-8 bottom-0 w-px bg-border" />
        )}

        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-0.5 text-purple-500">
            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
              <FileText className="h-4 w-4" />
            </div>
          </div>

          <div className={`flex-1 space-y-1 ${index < latestNotes.length - 1 ? 'pb-4' : 'pb-0'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className="text-sm leading-tight whitespace-pre-line">
                  {noteItem.content}
                </p>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDate(noteItem.createdAt)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {noteItem.createdBy.name}
            </p>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <CardTitle className="flex items-center gap-2 flex-shrink-0">
            <MessageSquare className="h-5 w-5" />
            Latest Notes
          </CardTitle>
          <div className="flex-1 flex items-center gap-2">
            <Input
              placeholder="Add a quick note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddNote();
                }
              }}
              className="h-9"
            />
            <Button size="sm" onClick={handleAddNote} disabled={!note.trim() || addNoteMutation.isPending} className="flex-shrink-0">
              Add Note
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 flex-1">

        {/* Notes Timeline */}
        <div className="space-y-4 pt-2">
          {renderNotesContent()}
        </div>
      </CardContent>
    </Card>
  );
}
